// FILE: src/App.js

import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase/config';
import { onAuthStateChanged, signOut, signInAnonymously } from "firebase/auth";
import { collection, query, where, onSnapshot, getDocs, writeBatch, doc, updateDoc, limit, arrayRemove, arrayUnion } from "firebase/firestore";
import { initialRestaurants } from './data/seed';
import logoHeader from './assets/logoheader.png'; // Import the header logo

// Import Components
import HomePage from './components/HomePage';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import BookingModal from './components/BookingModal';
import AuthModal from './components/AuthModal';
import { LayoutDashboard, LogIn, LogOut, ShieldCheck } from 'lucide-react';

const ADMIN_UID = "n1jkbAGEedNzBDSF3U9njdr3sWi2";

export default function App() {
    const [page, setPage] = useState({ name: 'home', data: null });
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [restaurants, setRestaurants] = useState([]);
    const [myRestaurant, setMyRestaurant] = useState(null);
    const [needsSeeding, setNeedsSeeding] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState({ restaurant: null, voucher: null });
    const [favorites, setFavorites] = useState([]);
    const [headerBg, setHeaderBg] = useState(false);
    
    const appId = 'goeatout-app';

    const changeHeaderBackground = () => {
        if (window.scrollY >= 80) {
            setHeaderBg(true);
        } else {
            setHeaderBg(false);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', changeHeaderBackground);
        return () => window.removeEventListener('scroll', changeHeaderBackground);
    }, []);

    const seedDatabase = async (firstUser = null) => {
        if (!db) return;
        console.log("Seeding database...");
        const restaurantsCollectionRef = collection(db, `/artifacts/${appId}/public/data/restaurants`);
        const batch = writeBatch(db);
        
        initialRestaurants.forEach((restaurant, index) => {
            const docRef = doc(restaurantsCollectionRef);
            let data = {...restaurant};
            if (index === 0 && firstUser) {
                data.ownerId = firstUser.uid;
            }
            batch.set(docRef, data);
        });

        await batch.commit();
        console.log("Database seeded successfully!");
    };

    useEffect(() => {
        if (!auth) return;
        const authAndListen = async () => {
            try {
                if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }
            } catch (error) { console.error("Anonymous sign-in failed:", error); }

            const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
                setUser(currentUser);
                setAuthReady(true);
            });
            return () => unsubscribe();
        };
        authAndListen();
    }, []);

    useEffect(() => {
        if (!db) return; 
        const restaurantsCollectionPath = `/artifacts/${appId}/public/data/restaurants`;
        const q = collection(db, restaurantsCollectionPath);
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (snapshot.empty) {
                setNeedsSeeding(true);
                setRestaurants([]);
            } else {
                const restaurantData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setNeedsSeeding(false);
                setRestaurants(restaurantData);
            }
        }, (error) => {
            console.error("ERROR in restaurant listener:", error);
            setNeedsSeeding(true); 
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (user && !user.isAnonymous && db) {
            const q = query(collection(db, `/artifacts/${appId}/public/data/restaurants`), where("ownerId", "==", user.uid));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                if (!snapshot.empty) {
                    const doc = snapshot.docs[0];
                    setMyRestaurant({ id: doc.id, ...doc.data() });
                } else {
                    setMyRestaurant(null);
                }
            });
            return () => unsubscribe();
        } else {
            setMyRestaurant(null);
        }
    }, [user, restaurants]);

    useEffect(() => {
        if (user && !user.isAnonymous && db) {
            const userDocRef = doc(db, `/artifacts/${appId}/public/data/users`, user.uid);
            const unsubscribe = onSnapshot(userDocRef, (doc) => {
                if (doc.exists()) {
                    setFavorites(doc.data().favorites || []);
                }
            });
            return () => unsubscribe();
        } else {
            setFavorites([]);
        }
    }, [user]);

    const toggleFavorite = async (restaurantId) => {
        if (!user || user.isAnonymous) {
            setShowAuthModal(true);
            return;
        }
        const userDocRef = doc(db, `/artifacts/${appId}/public/data/users`, user.uid);
        if (favorites.includes(restaurantId)) {
            await updateDoc(userDocRef, { favorites: arrayRemove(restaurantId) });
        } else {
            await updateDoc(userDocRef, { favorites: arrayUnion(restaurantId) });
        }
    };

    const handleAuthSuccess = async (authenticatedUser) => {
        setShowAuthModal(false);
        if (authenticatedUser.uid === ADMIN_UID) {
            setPage({ name: 'admin' });
            return;
        }
        setPage({ name: 'dashboard' });
        const userRestaurantQuery = query(collection(db, `/artifacts/${appId}/public/data/restaurants`), where("ownerId", "==", authenticatedUser.uid));
        const userRestaurantSnapshot = await getDocs(userRestaurantQuery);
        if (userRestaurantSnapshot.empty) {
            const unassignedQuery = query(collection(db, `/artifacts/${appId}/public/data/restaurants`), where("ownerId", "==", ""), limit(1));
            const unassignedSnapshot = await getDocs(unassignedQuery);
            if (!unassignedSnapshot.empty) {
                const restaurantToAssignDoc = unassignedSnapshot.docs[0];
                await updateDoc(restaurantToAssignDoc.ref, { ownerId: authenticatedUser.uid });
            } else if (needsSeeding) {
                await seedDatabase(authenticatedUser);
            }
        }
    };

    const handleLogout = async () => { 
        await signOut(auth);
        await signInAnonymously(auth).catch(err => console.error("Failed to sign in anonymously after logout:", err));
        setPage({ name: 'home' }); 
    };

    const handleCloseModal = () => setSelectedBooking({ restaurant: null, voucher: null });

    const isAdmin = user && user.uid === ADMIN_UID;
    const isRestaurantOwner = user && !user.isAnonymous && myRestaurant;

    const renderPage = () => {
        switch (page.name) {
            case 'dashboard':
                return isRestaurantOwner && !isAdmin ? <Dashboard user={user} myRestaurant={myRestaurant} setMyRestaurant={setMyRestaurant} /> : <HomePage user={user} favorites={favorites} toggleFavorite={toggleFavorite} restaurants={restaurants} />;
            case 'admin':
                return isAdmin ? <AdminDashboard needsSeeding={needsSeeding} seedDatabase={seedDatabase} /> : <HomePage user={user} favorites={favorites} toggleFavorite={toggleFavorite} restaurants={restaurants} />;
            default:
                return <HomePage user={user} favorites={favorites} toggleFavorite={toggleFavorite} restaurants={restaurants} />;
        }
    };

    if (!authReady) {
        return <div className="flex items-center justify-center h-screen">Loading App...</div>;
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans antialiased">
            <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${headerBg ? 'bg-white shadow-md' : 'bg-transparent'}`}>
                <nav className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
                    <div className="flex-1">
                        <img 
                            src={logoHeader} 
                            alt="GoEatOut Header Logo" 
                            className={`h-10 transition-opacity duration-300 ${headerBg ? 'opacity-100' : 'opacity-0'}`}
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        {user && !user.isAnonymous ? (
                            <>
                                {isAdmin && <button onClick={() => setPage({name: 'admin'})} className={`font-semibold flex items-center gap-2 ${headerBg ? 'text-red-600' : 'text-white'}`}><ShieldCheck size={18}/> Admin</button>}
                                {isRestaurantOwner && !isAdmin && <button onClick={() => setPage({name: 'dashboard'})} className={`font-semibold flex items-center gap-2 ${headerBg ? 'text-gray-600' : 'text-white'}`}><LayoutDashboard size={18}/> Dashboard</button>}
                                <button onClick={handleLogout} className={`font-semibold flex items-center gap-2 ${headerBg ? 'text-gray-600' : 'text-white'}`}><LogOut size={18}/> Log Out</button>
                            </>
                        ) : (
                            <button onClick={() => setShowAuthModal(true)} className={`font-semibold flex items-center gap-2 ${headerBg ? 'text-gray-600' : 'text-white'}`}><LogIn size={18}/> Restaurant Login</button>
                        )}
                    </div>
                </nav>
            </header>
            <main>{renderPage()}</main>
            {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onAuthSuccess={handleAuthSuccess} />}
            {selectedBooking.restaurant && <BookingModal restaurant={selectedBooking.restaurant} voucher={selectedBooking.voucher} onClose={handleCloseModal} user={user}/>}
            <footer className="bg-gray-800 text-white mt-16"><div className="container mx-auto py-6 px-8 text-center"><p>&copy; {new Date().getFullYear()} GoEatOut.co.uk. All rights reserved.</p></div></footer>
        </div>
    );
}
